import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { formatNumber } from '../../lib/utils';

const statusColors = {
  'Pending': 'bg-red-50 text-red-700 border-red-200',
  'Partially Billed': 'bg-amber-50 text-amber-700 border-amber-200',
  'Completed': 'bg-teal-50 text-teal-700 border-teal-200',
  'In Transport': 'bg-blue-50 text-blue-700 border-blue-200',
  'Issued for Delivery': 'bg-amber-50 text-amber-700 border-amber-200',
  'Delivered': 'bg-green-50 text-green-700 border-green-200'
};

const metricLabels = {
  'total_orders': 'All Orders',
  'pending_orders': 'Pending Orders',
  'partially_billed': 'Partially Billed Orders',
  'completed_orders': 'Completed Orders',
  'lr_in_transport': 'LRs In Transport',
  'lr_issued': 'LRs Issued for Delivery',
  'lr_delivered': 'LRs Delivered'
};

const DrillDownModal = ({ open, onClose, data, loading }) => {
  const navigate = useNavigate();

  if (!data) return null;

  const isLR = data.type === 'lr';
  const title = metricLabels[data.metric] || data.metric;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle data-testid="drilldown-title" className="text-lg font-bold text-slate-900">
            {title}
          </DialogTitle>
          <DialogDescription>
            {data.count} record{data.count !== 1 ? 's' : ''} found
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-slate-400">Loading...</div>
        ) : (
          <div data-testid="drilldown-table" className="overflow-x-auto">
            {isLR ? (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left py-2 px-3 font-semibold text-slate-700">LR Number</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-700">Order</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-700">Supplier</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-700">Transport</th>
                    <th className="text-right py-2 px-3 font-semibold text-slate-700">Bales</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.records.map((lr, i) => (
                    <tr key={lr.lr_id || i} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/grey-orders/${lr.order_id}`)}>
                      <td className="py-2 px-3 font-mono font-medium text-slate-900">{lr.lr_number || lr.lr_number_transport}</td>
                      <td className="py-2 px-3 text-slate-600">{lr.order_number || '-'}</td>
                      <td className="py-2 px-3 text-slate-600">{lr.supplier_name || '-'}</td>
                      <td className="py-2 px-3 text-slate-600">{lr.transport_name || '-'}</td>
                      <td className="py-2 px-3 text-right">{lr.bales_in_lr || '-'}</td>
                      <td className="py-2 px-3">
                        <Badge className={statusColors[lr.status] || ''}>{lr.status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="text-left py-2 px-3 font-semibold text-slate-700">Order #</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-700">Supplier</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-700">Item</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-700">Quality</th>
                    <th className="text-right py-2 px-3 font-semibold text-slate-700">Ordered</th>
                    <th className="text-right py-2 px-3 font-semibold text-slate-700">Billed</th>
                    <th className="text-right py-2 px-3 font-semibold text-slate-700">Received</th>
                    <th className="text-left py-2 px-3 font-semibold text-slate-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {data.records.map((order, i) => (
                    <tr key={order.order_id || i} className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer" onClick={() => navigate(`/grey-orders/${order.order_id}`)}>
                      <td className="py-2 px-3 font-mono font-medium text-slate-900">{order.order_number}</td>
                      <td className="py-2 px-3 text-slate-600">{order.supplier_name || '-'}</td>
                      <td className="py-2 px-3 text-slate-600">{order.item_name || '-'}</td>
                      <td className="py-2 px-3 text-slate-600">{order.quality_name || '-'}</td>
                      <td className="py-2 px-3 text-right font-medium">{formatNumber(order.ordered_quantity)} {order.order_unit}</td>
                      <td className="py-2 px-3 text-right">{formatNumber(order.total_billed_quantity)}</td>
                      <td className="py-2 px-3 text-right text-teal-700">{formatNumber(order.total_received_quantity)}</td>
                      <td className="py-2 px-3">
                        <Badge className={statusColors[order.order_status] || ''}>{order.order_status}</Badge>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {data.records.length === 0 && (
              <div className="py-8 text-center text-slate-400">No records found</div>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default DrillDownModal;
